import { useId, useMemo, useState } from 'react'
import { IoChevronDown } from 'react-icons/io5'
import { TbCopy, TbInfoCircle } from 'react-icons/tb'

import { buildAuditRows, type AuditRow } from '@/Ship/Helpers/auditInfo'
import { cx } from '@/Ship/Helpers/cssClass'
import { notify } from '@/Ship/Helpers/notify'

import styles from './ServiceInfo.module.css'

/** Стан акордеона спільний для всіх карток; localStorage може бути недоступний */
const OPEN_STORAGE_KEY = 'autostrada.detail.serviceInfoOpen'

const readOpen = (): boolean => {
    try {
        return window.localStorage.getItem(OPEN_STORAGE_KEY) === '1'
    } catch {
        return false
    }
}

const writeOpen = (open: boolean): void => {
    try {
        window.localStorage.setItem(OPEN_STORAGE_KEY, open ? '1' : '0')
    } catch {
        /* приватний режим тощо — стан просто не збережеться */
    }
}

const copyText = async (text: string): Promise<void> => {
    try {
        if (!navigator.clipboard) throw new Error('clipboard unavailable')
        await navigator.clipboard.writeText(text)
        notify.success('Скопійовано')
    } catch {
        notify.error('Не вдалося скопіювати')
    }
}

/** Підказка у згорнутому стані: `ID 12 · змінено 2026-09-30 10:02` — головне видно без розгортання */
const buildSummary = (rows: AuditRow[]): string => {
    const parts: string[] = []
    const id = rows.find((row) => row.key === 'id')
    if (id) parts.push(`ID ${id.value}`)
    const updated = rows.find((row) => row.key === 'updatedAt') ?? rows.find((row) => row.key === 'createdAt')
    if (updated) parts.push(`${updated.key === 'updatedAt' ? 'змінено' : 'створено'} ${updated.value}`)
    return parts.join(' · ')
}

/** Комірка: ідентифікатор — моноширинним «чіпом» з копіюванням; дата — з автором окремим рядком */
const Cell = ({ row }: { row: AuditRow }) => (
    <div className={cx(styles, 'cell')}>
        <dt className={cx(styles, 'label')}>{row.label}</dt>
        <dd className={cx(styles, 'value')}>
            {row.copyable ? (
                <span className={cx(styles, 'chip')}>
                    <span className={cx(styles, 'mono')}>{row.value}</span>
                    <button
                        type="button"
                        className={cx(styles, 'copy')}
                        aria-label={`Скопіювати ${row.label}`}
                        title="Скопіювати"
                        onClick={() => void copyText(row.value)}
                    >
                        <TbCopy aria-hidden="true" />
                    </button>
                </span>
            ) : (
                <>
                    <span className={cx(styles, 'date')}>{row.value}</span>
                    {row.author !== undefined && <span className={cx(styles, 'author')}>{row.author ?? '—'}</span>}
                </>
            )}
        </dd>
    </div>
)

interface ServiceInfoProps {
    /** Значення картки (formContext.values у plain-вигляді) */
    values: Record<string, unknown> | null | undefined
}

/**
 * «Службова інформація» внизу картки: id, uuid (з копіюванням), створено/змінено — коли
 * і ким. Окрема плашка-акордеон, згорнута за замовчуванням; нічого не рендерить, якщо
 * даних немає (напр. створення).
 */
export const ServiceInfo = ({ values }: ServiceInfoProps) => {
    const rows = useMemo(() => buildAuditRows(values), [values])
    const [open, setOpen] = useState(readOpen)
    const panelId = useId()

    if (rows.length === 0) return null

    const toggle = () => {
        const next = !open
        setOpen(next)
        writeOpen(next)
    }

    const summary = buildSummary(rows)

    return (
        <section className={cx(styles, 'section')} aria-label="Службова інформація">
            <button
                type="button"
                className={cx(styles, 'toggle')}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={toggle}
            >
                <TbInfoCircle className={cx(styles, 'icon')} aria-hidden="true" />
                <span className={cx(styles, 'title')}>Службова інформація</span>
                {summary && !open && <span className={cx(styles, 'summary')}>{summary}</span>}
                <IoChevronDown
                    className={`${cx(styles, 'chevron')} ${open ? cx(styles, 'chevronOpen') : ''}`}
                    aria-hidden="true"
                />
            </button>
            {/* Панель завжди в DOM: розгортання анімується через grid-template-rows */}
            <div id={panelId} className={`${cx(styles, 'panel')} ${open ? cx(styles, 'panelOpen') : ''}`}>
                <dl className={cx(styles, 'grid')} aria-hidden={!open}>
                    {rows.map((row) => (
                        <Cell key={row.key} row={row} />
                    ))}
                </dl>
            </div>
        </section>
    )
}
